// @ts-nocheck
/**
 * Ludipek B2B price cache.
 *
 * localStorage-backed cache for display prices fetched from POST /products/prices/fetch.
 * Keyed by (customer_id ?? 'public', store_country, region ?? 'all', product_id) so
 * switching account, country, or store location naturally routes to different keys — old
 * entries sit idle, they don't contaminate. An entry is fresh for TTL_MS and still paintable
 * (stale) for STALE_MS. Prunes unusable entries on every page load.
 *
 * Exposes window.LudipekB2BCache = { get, set, getMany, setMany, markStale, invalidateAll }.
 */
(function () {
  const KEY_PREFIX = 'ludipek:price:';
  // 10min. History: 5min → 30min (when a cold 24-product PLP fetch cost ~4s of server-side
  // SuiteQL) → 10min. The server-side batch endpoint now resolves a cold 24-item miss in ~0.2s
  // and its own 15min TTL shields NetSuite, so a shorter client TTL costs only cheap API calls
  // (mostly server-cache hits) — NOT extra NetSuite load — while tightening how stale a displayed
  // price can get after a NetSuite price change. Going below the server's 15min TTL has
  // diminishing returns (the server cache is the freshness floor), so 10min is the sweet spot.
  // Entries are keyed by (customer, country, region, product) so account/region switches always
  // route to fresh keys regardless of TTL. The charged price is always the signed cart price, so
  // this only affects display freshness, never what the buyer pays.
  const TTL_MS = 10 * 60 * 1000;
  // Stale-while-revalidate window. Past TTL_MS an entry stops being authoritative, but it stays
  // *usable* for this much longer: the buyer sees the last known price instantly while the fresh
  // one is fetched behind them and swapped in.
  //
  // This exists because an expired entry used to mean a shimmer and a wait. The pricing API answers
  // a warm batch in ~200ms but a cold one — new server instance, or its own 15-minute TTL rolling
  // over — in ~1.5s, and the buyer is in Chile while the service runs in us-central1, so add a round
  // trip on top. Painting a slightly-old number and correcting it is strictly better than painting
  // nothing for a second and a half.
  //
  // Safe by construction: this is a DISPLAY cache. What the buyer is charged is the signed cart
  // price, resolved server-side at /cart/sign, never this value. Twelve hours is the outer bound
  // because prices barely move intra-day and the nightly sync gives everything a fresh baseline.
  const STALE_MS = 12 * 60 * 60 * 1000;

  function logDebug(message, details) {
    if (details === undefined) {
      console.debug('[b2b-cache]', message);
    } else {
      console.debug('[b2b-cache]', message, details);
    }
  }

  function cacheKey(customerId, storeCountry, region, productId) {
    const customerPart = customerId == null ? 'public' : String(customerId);
    const regionPart = region || 'all';
    return `${KEY_PREFIX}${customerPart}:${storeCountry}:${regionPart}:${productId}`;
  }

  function readEntry(key) {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return null;
      const entry = JSON.parse(raw);
      if (!entry || typeof entry.fetchedAt !== 'number') return null;
      return entry;
    } catch (error) {
      logDebug('failed to read cache entry', { key, error: String(error) });
      return null;
    }
  }

  // `revalidate` is set by markStale: the entry keeps its real fetchedAt (so the STALE_MS bound still
  // counts from the actual fetch) but is no longer authoritative.
  function isFresh(entry, now) {
    return entry !== null && entry.revalidate !== true && now - entry.fetchedAt <= TTL_MS;
  }

  function isUsable(entry, now) {
    return entry !== null && now - entry.fetchedAt <= STALE_MS;
  }

  function isOldFormatKey(key) {
    // Old format: ludipek:price:<customer>:<country>:<product> (3 colons after prefix)
    // New format: ludipek:price:<customer>:<country>:<region>:<product> (4 colons after prefix)
    const afterPrefix = key.slice(KEY_PREFIX.length);
    return afterPrefix.split(':').length < 4;
  }

  function pruneExpired() {
    const now = Date.now();
    const keysToRemove = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key === null || !key.startsWith(KEY_PREFIX)) continue;
      // Prune on the stale bound, not the fresh one — an entry past TTL is still worth painting.
      if (isOldFormatKey(key) || !isUsable(readEntry(key), now)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    if (keysToRemove.length > 0) {
      logDebug(`pruned ${keysToRemove.length} expired/legacy entries`);
    }
  }

  function get(customerId, storeCountry, region, productId) {
    const entry = readEntry(cacheKey(customerId, storeCountry, region, productId));
    if (!isFresh(entry, Date.now())) return null;
    return entry;
  }

  function set(customerId, storeCountry, region, productId, value) {
    const entry = {
      fetchedAt: Date.now(),
      unit_price: value.unit_price,
      currency_id: value.currency_id,
      currency_name: value.currency_name,
      price_source: value.price_source,
    };
    try {
      localStorage.setItem(cacheKey(customerId, storeCountry, region, productId), JSON.stringify(entry));
    } catch (error) {
      logDebug('failed to write cache entry (likely quota exceeded)', { error: String(error) });
    }
  }

  /**
   * Split a batch of product ids three ways.
   *
   * `hits` are fresh and need no network. `stale` are past TTL but still paintable — render them
   * immediately AND refetch them. `misses` have nothing to show and must be fetched before a price
   * can appear. Callers that only care about "do I need the network" should use
   * `misses.concat(Object.keys(stale))`.
   */
  function getMany(customerId, storeCountry, region, productIds) {
    const now = Date.now();
    const hits = {};
    const stale = {};
    const misses = [];
    productIds.forEach((productId) => {
      const entry = readEntry(cacheKey(customerId, storeCountry, region, productId));
      if (isFresh(entry, now)) {
        hits[productId] = entry;
      } else if (isUsable(entry, now)) {
        stale[productId] = entry;
      } else {
        misses.push(productId);
      }
    });
    return { hits, stale, misses };
  }

  function setMany(customerId, storeCountry, region, lines) {
    lines.forEach((line) => {
      if (line && line.status === 'ok') {
        set(customerId, storeCountry, region, line.product_id, line);
      }
    });
  }

  /**
   * Demote these products' entries from fresh to stale, without deleting them.
   *
   * Called after a cart is signed: the server just priced those products, which is the cheapest hint
   * we get that one of their display prices may have moved. A stale entry still paints on the next
   * render, and it is refetched behind the buyer then. Deleting entries here (the old invalidateAll
   * after every sign) turned every cart re-render into a cold fetch behind a skeleton. Entries that
   * are already stale or gone are left alone.
   */
  function markStale(customerId, storeCountry, region, productIds) {
    const now = Date.now();
    let demoted = 0;
    productIds.forEach((productId) => {
      const key = cacheKey(customerId, storeCountry, region, productId);
      const entry = readEntry(key);
      if (!isFresh(entry, now)) return;
      try {
        localStorage.setItem(key, JSON.stringify({ ...entry, revalidate: true }));
        demoted += 1;
      } catch (error) {
        logDebug('failed to mark cache entry stale', { key, error: String(error) });
      }
    });
    if (demoted > 0) {
      logDebug(`marked ${demoted} of ${productIds.length} entries stale for revalidation`);
    }
  }

  function invalidateAll() {
    const keysToRemove = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key !== null && key.startsWith(KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    logDebug(`invalidated ${keysToRemove.length} entries`);
  }

  // Defer the localStorage scan until the main thread is idle so it never blocks first paint.
  // Safari has no requestIdleCallback; fall back to a setTimeout so the prune still runs.
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(pruneExpired, { timeout: 2000 });
  } else {
    setTimeout(pruneExpired, 0);
  }

  window.LudipekB2BCache = {
    get,
    set,
    getMany,
    setMany,
    markStale,
    invalidateAll,
    ttlMs: TTL_MS,
    staleMs: STALE_MS,
  };
})();
